import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFactory } from "@/core/hooks/useFactory";
import { useLanguage } from "@/core/LanguageContext";
import { useAuth } from "@/core/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import {
  LayoutDashboard,
  ClipboardCheck,
  HelpCircle,
  ListChecks,
  Settings,
  AlertCircle,
} from "lucide-react";
import QuestionBankPage from "../components/QuestionBankPage";
import FactorySettingsPage from "../components/FactorySettingsPage";
import AuditListPage from "../components/AuditListPage";
import AuditFormDialog from "../components/AuditFormDialog";
import { useCreateAudit } from "../hooks/useAudits";
import MyAuditsPage from "../components/MyAuditsPage";
import { useInstancesByAuditorId } from "../hooks/useScheduleInstances";

function getWeekEnd(now: Date): Date {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const dayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dayOffset);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

export default function LpaManagement() {
  const { selectedFactory } = useFactory();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "dashboard";
  const normalizedTab = activeTab === 'schedules' ? 'my-audits' : activeTab;

  const { data: openInstances } = useInstancesByAuditorId(profile?.id, {
    statuses: ['pending', 'in_progress', 'overdue'],
  });

  const myAuditsIndicator = useMemo(() => {
    const nowMs = Date.now();
    const weekEndMs = getWeekEnd(new Date()).getTime();

    const overdueCount = (openInstances ?? []).filter(
      (instance) => new Date(instance.due_date).getTime() < nowMs,
    ).length;

    if (overdueCount > 0) {
      return { tone: 'overdue' as const, count: overdueCount };
    }

    const outstandingThisWeek = (openInstances ?? []).some((instance) => {
      const dueMs = new Date(instance.due_date).getTime();
      return dueMs >= nowMs && dueMs <= weekEndMs;
    });

    if (outstandingThisWeek) {
      return { tone: 'outstanding' as const, count: 0 };
    }

    return { tone: 'clear' as const, count: 0 };
  }, [openInstances]);

  // Audit create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const createAudit = useCreateAudit();

  const handleCreateAudit = (data: {
    name: string;
    description: string;
    owner_id: string;
    area_id: string | null;
    line_id: string | null;
    station_id: string | null;
  }) => {
    createAudit.mutate(
      {
        name: data.name,
        description: data.description || null,
        owner_id: data.owner_id,
        area_id: data.area_id,
        line_id: data.line_id,
        station_id: data.station_id,
      },
      { onSuccess: () => setCreateDialogOpen(false) },
    );
  };

  const updateParams = (updates: Record<string, string | null>) => {
    const nextParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
    });
    setSearchParams(nextParams);
  };

  if (!selectedFactory) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">
            {t("mod_lpa.management.title", "LPA Audits")}
          </h1>
          <p className="text-muted-foreground">
            {t("mod_lpa.management.subtitle", "Layered Process Audit management per AIAG CQI-8")}
          </p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">
              {t("mod_lpa.management.noFactory", "No factory selected")}
            </h3>
            <p className="text-muted-foreground text-sm">
              {t("mod_lpa.management.noFactoryDescription", "Select a factory to manage LPA audits.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          {t("mod_lpa.management.title", "LPA Audits")}
        </h1>
        <p className="text-muted-foreground">
          {t("mod_lpa.management.subtitle", "Layered Process Audit management per AIAG CQI-8")}
        </p>
      </div>

      <Tabs
        value={normalizedTab}
        onValueChange={(value) => updateParams({ tab: value })}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <LayoutDashboard className="h-4 w-4" />
            {t("mod_lpa.management.tabs.dashboard", "Dashboard")}
          </TabsTrigger>
          <TabsTrigger value="audits" className="gap-2">
            <ClipboardCheck className="h-4 w-4" />
            {t("mod_lpa.management.tabs.audits", "Audits")}
          </TabsTrigger>
          <TabsTrigger value="questions" className="gap-2">
            <HelpCircle className="h-4 w-4" />
            {t("mod_lpa.management.tabs.questions", "Questions")}
          </TabsTrigger>
          <TabsTrigger value="my-audits" className="gap-2">
            <ListChecks className="h-4 w-4" />
            <span className="relative inline-flex pr-2">
              {t("mod_lpa.management.tabs.myAudits", "My Audits")}
              {myAuditsIndicator.tone === 'overdue' ? (
                <span className="absolute -top-2 -right-4 inline-flex min-w-5 h-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">
                  {myAuditsIndicator.count}
                </span>
              ) : (
                <span
                  className={`absolute -top-1 -right-2 inline-flex h-2.5 w-2.5 rounded-full ${
                    myAuditsIndicator.tone === 'outstanding' ? 'bg-orange-500' : 'bg-emerald-500'
                  }`}
                />
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            {t("mod_lpa.management.tabs.settings", "Settings")}
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* 
            Dashboard will show:
            - % audits completed by layer (per_shift, daily, weekly, monthly, quarterly)
            - Conformance rate by area/line/station
            - Non-conformance trends over time
            - Overdue/missed audit instances
            - Top non-conforming questions
            - KPIs per AIAG CQI-8 metrics
          */}
          <Card>
            <CardHeader>
              <CardTitle>{t("mod_lpa.dashboard.title", "Audit Overview")}</CardTitle>
              <CardDescription>
                {t("mod_lpa.dashboard.description", "Completion rates, conformance trends, and key metrics across all audit layers.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <LayoutDashboard className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-sm">
                {t("mod_lpa.dashboard.placeholder", "Dashboard metrics will appear here once audits are configured and completed.")}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audits Tab */}
        <TabsContent value="audits" className="space-y-4">
          <AuditListPage onCreateAudit={() => setCreateDialogOpen(true)} />
          <AuditFormDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            onSubmit={handleCreateAudit}
            isSubmitting={createAudit.isPending}
          />
        </TabsContent>

        {/* Questions Tab */}
        <TabsContent value="questions" className="space-y-4">
          <QuestionBankPage />
        </TabsContent>

        {/* My Audits Tab */}
        <TabsContent
          value="my-audits"
          className="space-y-4 data-[state=inactive]:hidden"
          forceMount
        >
          <MyAuditsPage />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <FactorySettingsPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
